import {
  Component,
  signal,
  ChangeDetectionStrategy,
  output,
  input,
  viewChild,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { Popover, PopoverModule } from 'primeng/popover';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { PushDeviceEntry } from '@tourfold/libraries/tf-api-client-frontend-v2';
import { CloseOverlayOnNavigationDirective } from '@tourfold/libraries/shared-ui';

export type DevicePlatformValue = NonNullable<PushDeviceEntry['platformType']>;

export interface PushDeviceFilterValues {
  platformType: DevicePlatformValue | null;
}

@Component({
  selector: 'tf-settings-push-device-filters',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    SelectModule,
    PopoverModule,
    TranslateModule,
    TooltipModule,
    CloseOverlayOnNavigationDirective,
  ],
  templateUrl: './push-device-filters.html',
  styleUrl: './push-device-filters.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PushDeviceFilters {
  readonly platformType = input<DevicePlatformValue | null>(null);
  readonly hasActiveFilters = input<boolean>(false);
  readonly isLoading = input<boolean>(false);

  readonly filterApplied = output<PushDeviceFilterValues>();
  readonly filterCleared = output();

  readonly filterPopover = viewChild<Popover>('filterPopover');

  readonly localPlatformType = signal<DevicePlatformValue | null>(null);

  readonly platformOptions: { label: string; value: DevicePlatformValue }[] = [
    { label: 'SETTINGS.PUSH_NOTIFICATIONS.PLATFORM_IOS', value: 'IOS' },
    { label: 'SETTINGS.PUSH_NOTIFICATIONS.PLATFORM_ANDROID', value: 'ANDROID' },
    { label: 'SETTINGS.PUSH_NOTIFICATIONS.PLATFORM_UNKNOWN', value: 'UNKNOWN' },
  ];

  constructor() {
    effect(() => {
      this.localPlatformType.set(this.platformType());
    });
  }

  onPlatformTypeChange(platformType: DevicePlatformValue | null): void {
    this.localPlatformType.set(platformType);
  }

  applyFilters(): void {
    this.filterApplied.emit({
      platformType: this.localPlatformType(),
    });
    this.filterPopover()?.hide();
  }

  clearFilters(): void {
    this.localPlatformType.set(null);
    this.filterCleared.emit();
    this.filterPopover()?.hide();
  }

  toggleFilterPopover(event: Event): void {
    this.filterPopover()?.toggle(event);
  }
}
